import { createDraftSafeSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '@src/redux/store';
import { CampaignGuideData } from '@src/types/campaign';
import { FileInfo } from '@src/types/product';
import { ProjectStatusType } from '@src/types/project';
import { getProjectGuide, updateProjectGuide } from './guideDetailThunk';

export interface GuideState {
  loading: boolean;
  modal: 'off' | 'save' | 'completeReview' | 'completeTranslation';
  data: {
    id: number;
    name: string;
    brandKo: string;
    brandEn: string;
    status: ProjectStatusType;
    serviceId: string;
    user: { id: number; name: string };
    type: string;

    purchaseUrl: string;
    description: string;
    descriptionEn: string;
    reviewGuide: string;
    reviewGuideEn: string;
    files: FileInfo[];
    products: { id?: number; name: string }[];
    campaigns: CampaignGuideData[];
  };
}

const initialState: GuideState = {
  loading: false,
  modal: 'off',
  data: {
    id: 0,
    name: '',
    brandKo: '',
    brandEn: '',
    status: 'payment',
    serviceId: '',
    user: { id: 0, name: '' },
    type: '',

    purchaseUrl: '',
    description: '',
    descriptionEn: '',
    reviewGuide: '',
    reviewGuideEn: '',
    files: [],
    products: [],
    campaigns: [],
  },
};

const guideDetailSlice = createSlice({
  name: 'guideDetail',
  initialState,
  reducers: {
    onChangeModal: (state, { payload }) => {
      state.modal = payload;
    },
    onCleanUp: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProjectGuide.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProjectGuide.fulfilled, (state, { payload }) => {
        const {
          id,
          serviceId,
          status,
          type,
          name,
          user,
          brandKo,
          brandEn,
          purchaseUrl,
          description,
          descriptionEn,
          reviewGuide,
          reviewGuideEn,
          products,
          files,
          campaigns,
        } = payload;

        state.data = {
          id,
          serviceId,
          status,
          type,
          name,
          user,
          brandKo,
          brandEn,
          purchaseUrl,
          description,
          descriptionEn,
          reviewGuide,
          reviewGuideEn,
          products,
          files,
          campaigns,
        };

        state.loading = false;
      });

    builder
      .addCase(updateProjectGuide.fulfilled, (state, { payload }) => {
        const {
          id,
          serviceId,
          status,
          type,
          name,
          user,
          brandKo,
          brandEn,
          purchaseUrl,
          description,
          descriptionEn,
          reviewGuide,
          reviewGuideEn,
          products,
          files,
          campaigns,
        } = payload;

        state.data = {
          id,
          serviceId,
          status,
          type,
          name,
          user,
          brandKo,
          brandEn,
          purchaseUrl,
          description,
          descriptionEn,
          reviewGuide,
          reviewGuideEn,
          products,
          files,
          campaigns,
        };
      })
      .addCase(updateProjectGuide.rejected, (_, { payload }) => {
        throw payload;
      });
  },
});

export const { onChangeModal, onCleanUp } = guideDetailSlice.actions;
export default guideDetailSlice.reducer;

/* Draft Selector */
const selectSelf = (state: RootState) => state.guideDetail;

export const projectDataSelector = createDraftSafeSelector(
  selectSelf,
  (state) => {
    const { id, name, brandKo, brandEn, status, serviceId, user, type } =
      state.data;

    return {
      id,
      name,
      brandKo,
      brandEn,
      status,
      serviceId,
      user,
      type,
    };
  }
);

export const guideDataSelector = createDraftSafeSelector(
  selectSelf,
  (state) => {
    const {
      purchaseUrl,
      description,
      descriptionEn,
      reviewGuide,
      reviewGuideEn,
      files,
      products,
      campaigns,
    } = state.data;

    return {
      purchaseUrl,
      description,
      descriptionEn,
      reviewGuide,
      reviewGuideEn,
      files,
      products,
      campaigns,
    };
  }
);
